import { HttpErrorResponse } from "@angular/common/http";
import type { ErrorEvent, EventHint } from "@sentry/angular";

import { MissingCredentialsError } from "@app/core/shared/domain/missing-credentials.error";
import { NoInternetError } from "@app/core/shared/data/network/errors/no-internet.error";

/**
 * Names of error classes that represent expected, non-actionable situations
 * (the app already handles them: it shows a message to the user or redirects to
 * login/logout). Events whose underlying error matches one of these are dropped
 * before being sent to Sentry.
 *
 * This is the single place to decide what is NOT worth reporting. Add a new
 * entry here together with a short note explaining WHY it is non-actionable.
 */
const NON_ACTIONABLE_ERROR_NAMES: ReadonlySet<string> = new Set<string>([
  // Thrown whenever there is no auth token available (session expired / invalid
  // token / user not logged in). It is raised across dozens of use-cases as a
  // control-flow signal; the app reacts by sending the user to login, so it is
  // never an actual bug worth reporting.
  MissingCredentialsError.name,
  // Raised when a request fails with HTTP status 0 (device offline / connection
  // dropped / request aborted). The app already reacts by redirecting the user
  // to the no-internet fallback page, so it is a connectivity condition, not a
  // bug worth reporting.
  NoInternetError.name,
]);

/**
 * Substrings of event messages that represent non-actionable noise: either from
 * third-party SDKs, or from our own logs that report a failure outside the app's
 * control (e.g. an external asset that the backend serves). Use this when the
 * error class name is too generic to match on (e.g. a plain `Error`) or the
 * event is a captured console message rather than a thrown exception.
 *
 * Add a new entry together with a short note explaining WHY it is non-actionable.
 */
const NON_ACTIONABLE_ERROR_MESSAGE_PATTERNS: readonly string[] = [
  // Internal assertion raised by the Firebase Auth web SDK from inside its
  // popup/redirect machinery (AbstractPopupRedirectOperation) when a stale auth
  // event is delivered after the operation already resolved and cleaned up. The
  // third-party sign-in itself succeeds (signInWithIdp returns 200); this fires
  // asynchronously inside firebase-js-sdk afterwards and is a long-standing,
  // known SDK race we cannot reliably prevent from app code. Pure noise.
  "INTERNAL ASSERTION FAILED: Pending promise was never set",
  // Logged by the story video players (console.warn) when a <video> element
  // fails to load its source. The player only renders the signed CloudFront URL
  // the backend provides and already degrades gracefully (hides the spinner); a
  // failure means the clip is missing/not-yet-generated or the signed URL is
  // expired/invalid — a backend/content issue, not a frontend bug to report.
  "Video failed to load:",
  // Network failure ("Load failed" / "Failed to fetch") raised by the Firebase
  // Analytics SDK when it POSTs to the Firebase Installations endpoint to
  // register an installation ID. The request is commonly blocked by the user's
  // environment (Safari ITP, content/ad blockers, corporate proxies, offline),
  // none of which we control; Analytics simply degrades. Third-party SDK
  // connectivity noise, not a frontend bug.
  "firebaseinstallations.googleapis.com",
  // console.warn from the Firebase Analytics SDK when it cannot fetch the
  // dynamic config (measurement ID) from firebase.googleapis.com — same blocked/
  // offline environments as above. The SDK explicitly recovers by falling back
  // to the measurement ID baked into our local Firebase config (it says so in
  // the message), so Analytics keeps working. Non-actionable third-party noise.
  "Failed to fetch this Firebase app's measurement ID",
  // FirebaseError from the Installations SDK when refreshing/generating the auth
  // token fails (e.g. "404 NOT_FOUND: Requested entity was not found" because
  // the locally-cached FID was deleted/expired server-side, or a network
  // failure). It is internal plumbing for Analytics/FCM; the SDK recovers by
  // re-registering the installation. The error code is stable across the various
  // underlying causes, so match on it. Non-actionable third-party noise.
  "(installations/request-failed)",
  // InvalidStateError thrown by the Firebase Analytics/Installations SDK's
  // internal IndexedDB access when the DB connection is already closing —
  // typically because the page is navigating/unloading mid-transaction. We do
  // not use IndexedDB ourselves (our DAOs use localStorage), so this can only
  // come from the third-party SDK and is a benign teardown race, not a bug.
  "The database connection is closing",
  // Literal placeholder produced by @sentry/angular's ErrorHandler when the
  // value thrown is not extractable into a real error (null/undefined/{} or a
  // non-Error promise rejection): it captures the string "Handled unknown
  // error". The resulting event has no message and no stack, so there is
  // nothing to act on. Per breadcrumbs these come from offline lazy-chunk
  // dynamic import() failures ("Importing a module script failed"), a
  // connectivity condition we don't control. No real app error contains this
  // exact string, so dropping it is safe.
  "Handled unknown error",
];

/**
 * Substrings that identify a stack-frame filename as belonging to code we do
 * NOT ship: browser extensions and injected userscripts (e.g. Tampermonkey/
 * Greasemonkey, password managers, in-app-browser injected scripts). When the
 * crashing frame lives in one of these, the error originates from third-party
 * code running in the user's browser, not from our app, so it is unfixable on
 * our side and pure noise (e.g. a userscript's "Failed to get initial setup /
 * an unknown error occurred" raised from `request@user-script:…`).
 */
const NON_ACTIONABLE_FRAME_PATTERNS: readonly string[] = [
  "user-script",
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "safari-web-extension://",
];

/**
 * Returns true when any frame of the event's exception stack trace originates
 * from a browser extension or injected userscript (see
 * NON_ACTIONABLE_FRAME_PATTERNS).
 */
function isFromBrowserExtension(event: ErrorEvent): boolean {
  const frames = event.exception?.values?.flatMap(
    (value) => value.stacktrace?.frames ?? []
  );
  return (
    frames?.some((frame) => {
      const filename = frame.filename ?? "";
      return NON_ACTIONABLE_FRAME_PATTERNS.some((pattern) =>
        filename.includes(pattern)
      );
    }) ?? false
  );
}

/**
 * Returns true when the event's underlying error is an HTTP failure with status
 * 0 (device offline / connection dropped / request aborted). Our API client
 * converts these into NoInternetError (already filtered above), but raw
 * HttpClient callers that bypass it — e.g. ngx-translate's TranslateHttpLoader
 * fetching ./assets/i18n/*.json — surface the original HttpErrorResponse
 * ("Http failure response for …: 0 Unknown Error") straight to Sentry. It is
 * the same non-actionable connectivity condition, so drop it too.
 */
function isOfflineHttpError(event: ErrorEvent, hint?: EventHint): boolean {
  const original = hint?.originalException;
  if (original instanceof HttpErrorResponse && original.status === 0) {
    return true;
  }
  // @sentry/angular's ErrorHandler unwraps an HttpErrorResponse via
  // extractHttpModuleError, which for a status-0 network failure captures the
  // plain message string ("Http failure response for <url>: 0 Unknown Error")
  // rather than the HttpErrorResponse instance — so the instanceof check above
  // never matches it. Match Angular's status-0 message shape too. Status 0 is
  // always a connectivity condition (offline / aborted / blocked), never an
  // actionable frontend bug.
  const message = resolveErrorMessage(event, hint);
  return (
    message.startsWith("Http failure response for") &&
    message.includes(": 0 Unknown Error")
  );
}

/**
 * Returns true for a "Maximum call stack size exceeded" RangeError whose stack
 * has no frame pointing at our own code. Google/Chrome Translate (it wraps text
 * in nested <font> tags) and some extensions reprocess the DOM Angular keeps
 * mutating and recurse inside their own injected script, surfacing a single
 * unmappable frame (e.g. `undefined:10:73`). That is third-party code we don't
 * ship and cannot fix. A genuine recursion in our code carries app frames
 * (src/app/…) and is intentionally NOT matched here, so it still reaches Sentry.
 */
function isThirdPartyStackOverflow(event: ErrorEvent): boolean {
  const values = event.exception?.values ?? [];
  const isStackOverflow = values.some(
    (value) =>
      value.type === "RangeError" &&
      (value.value ?? "").includes("Maximum call stack size exceeded")
  );
  if (!isStackOverflow) {
    return false;
  }
  const frames = values.flatMap((value) => value.stacktrace?.frames ?? []);
  const hasAppFrame = frames.some((frame) => frame.in_app === true);
  return !hasAppFrame;
}

/**
 * Returns true when the event is a NotAllowedError raised by HTMLMediaElement
 * .play() (its only stack frame is the native `play`). The browser rejects
 * play() under its autoplay policy — no preceding user gesture, low-power mode,
 * or the user/platform denied playback — none of which the app controls; it
 * already degrades gracefully (shows the play button). We narrow on the `play`
 * frame on purpose so we still report a genuinely actionable NotAllowedError
 * from other APIs we use (e.g. navigator.share() called without a user gesture).
 */
function isMediaAutoplayBlocked(event: ErrorEvent): boolean {
  return (
    event.exception?.values?.some(
      (value) =>
        value.type === "NotAllowedError" &&
        (value.stacktrace?.frames ?? []).some(
          (frame) => frame.function === "play"
        )
    ) ?? false
  );
}

/**
 * Returns the error class name for an event, preferring the original thrown
 * exception (reliable for our custom Error subclasses that set `name`) and
 * falling back to the exception type captured in the event payload.
 */
function resolveErrorName(event: ErrorEvent, hint?: EventHint): string {
  const original = hint?.originalException;
  if (original instanceof Error && original.name) {
    return original.name;
  }
  return event.exception?.values?.[0]?.type ?? "";
}

/**
 * Returns the error message for an event, preferring the original thrown
 * exception and falling back to the exception value captured in the payload.
 */
function resolveErrorMessage(event: ErrorEvent, hint?: EventHint): string {
  const original = hint?.originalException;
  if (original instanceof Error && original.message) {
    return original.message;
  }
  // Console-captured events (captureConsoleIntegration) carry the text in
  // `event.message`; thrown exceptions carry it in the exception value.
  return event.message ?? event.exception?.values?.[0]?.value ?? "";
}

/**
 * Sentry `beforeSend` hook: drop non-actionable events, let everything else
 * through unchanged. Returning `null` discards the event.
 */
export function beforeSend(
  event: ErrorEvent,
  hint: EventHint
): ErrorEvent | null {
  if (NON_ACTIONABLE_ERROR_NAMES.has(resolveErrorName(event, hint))) {
    return null;
  }
  if (isFromBrowserExtension(event)) {
    return null;
  }
  if (isOfflineHttpError(event, hint)) {
    return null;
  }
  if (isMediaAutoplayBlocked(event)) {
    return null;
  }
  if (isThirdPartyStackOverflow(event)) {
    return null;
  }
  const message = resolveErrorMessage(event, hint);
  if (
    NON_ACTIONABLE_ERROR_MESSAGE_PATTERNS.some((pattern) =>
      message.includes(pattern)
    )
  ) {
    return null;
  }
  return event;
}
